import { createContext, type ReactNode, useContext } from 'react';

import type { PostingsV3Mode } from '@revelio/data-access';

/** The two values of the Metric Mode filter (`METRIC_MODE_OPTIONS`). */
export type PostingsMetricMode = 'posting' | 'expected_hires';

/** `generate_chart_config`'s model-filled `postings_mode`. */
export type AssistantPostingsMode = 'normal' | 'hires';

const DefaultPostingsMetricModeContext = createContext<
  PostingsMetricMode | undefined
>(undefined);

/**
 * Overrides `PostingDataProvider`'s `revelioPageId`-based default, which
 * weights by expected hires on any page without one — the assistant's
 * synthetic chart page included.
 */
export function DefaultPostingsMetricModeProvider({
  mode,
  children,
}: {
  mode: PostingsMetricMode;
  children: ReactNode;
}) {
  return (
    <DefaultPostingsMetricModeContext.Provider value={mode}>
      {children}
    </DefaultPostingsMetricModeContext.Provider>
  );
}

export const useDefaultPostingsMetricModeOverride = ():
  | PostingsMetricMode
  | undefined => useContext(DefaultPostingsMetricModeContext);

export const toPostingsMetricMode = (
  mode: AssistantPostingsMode | undefined
): PostingsMetricMode => (mode === 'hires' ? 'expected_hires' : 'posting');

const RequestedPostingsModeContext = createContext<PostingsV3Mode | undefined>(
  undefined
);

/** The mode the postings provider actually sent; the digest labels from it. */
export const RequestedPostingsModeProvider =
  RequestedPostingsModeContext.Provider;

export const useRequestedPostingsMode = (): PostingsV3Mode | undefined =>
  useContext(RequestedPostingsModeContext);
